import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { analyseTaxDeductions } from '@/lib/tax-maximiser';
import { formatAUD } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Receipt, Lightbulb, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function TaxDeductionMaximiser() {
  const { data } = useAuth();
  const navigate = useNavigate();

  const summary = useMemo(
    () => analyseTaxDeductions(data.transactions, data.vehicles, data.vehicleExpenses),
    [data.transactions, data.vehicles, data.vehicleExpenses],
  );

  if (summary.tips.length === 0 && summary.totalIncome === 0) return null;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">Tax Deduction Tips</h3>
          </div>
          <Badge variant="secondary" className="text-[10px]">
            FY {summary.fyLabel}
          </Badge>
        </div>

        {/* Summary stats */}
        <div className="mb-3 flex gap-3 text-center">
          <div className="flex-1 rounded-lg bg-muted/50 p-2">
            <p className="text-[10px] text-muted-foreground">Claimed</p>
            <p className="font-display text-sm font-bold">{formatAUD(summary.totalDeductions)}</p>
          </div>
          <div className="flex-1 rounded-lg bg-muted/50 p-2">
            <p className="text-[10px] text-muted-foreground">Rate</p>
            <p className="font-display text-sm font-bold">{summary.deductionRate.toFixed(1)}%</p>
          </div>
        </div>

        {/* Tips */}
        <div className="space-y-2">
          {summary.tips.slice(0, 3).map(tip => (
            <div key={tip.id} className="rounded-lg border border-primary/10 bg-primary/5 p-2.5">
              <div className="mb-1 flex items-start gap-2">
                <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium">{tip.title}</p>
                  <p className="mt-0.5 text-[10px] leading-relaxed text-muted-foreground">
                    {tip.description}
                  </p>
                  {tip.potentialSaving && (
                    <Badge variant="outline" className="mt-1 text-[10px] text-primary border-primary/30">
                      {tip.potentialSaving}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {summary.tips.length > 3 && (
          <button
            onClick={() => navigate('/more/tax')}
            className="mt-2 flex w-full items-center justify-center gap-1 text-[10px] font-medium text-primary"
          >
            View all {summary.tips.length} tips
            <ChevronRight className="h-3 w-3" />
          </button>
        )}
      </CardContent>
    </Card>
  );
}
